import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CreditCard, FileText, Landmark, Link2, MessageSquare, Plus, ReceiptText, Scale, SlidersHorizontal, X } from "lucide-react";
import { useChat } from "@/components/chat/ChatProvider";
import { usePeriodLink } from "@/books/period";
import { cn } from "@/lib/utils";

const AGENT_PROMPT = "What's uncategorised?";

const storageKey = (userId: string | undefined) => `dolfin-demo-tasks-dismissed:${userId ?? "anon"}`;

function dismissed(userId: string | undefined) {
  try {
    return localStorage.getItem(storageKey(userId)) === "1";
  } catch {
    return false;
  }
}

/** D14: guided tasks on Books home in a demo, each opening the real screen. Dismissed per person, in this browser. */
export function TryItYourself({ userId }: { userId: string | undefined }) {
  const [hidden, setHidden] = useState(() => dismissed(userId));
  const withPeriod = usePeriodLink();
  const { openChat } = useChat();
  if (hidden) return null;

  function dismiss() {
    setHidden(true);
    try {
      localStorage.setItem(storageKey(userId), "1");
    } catch {
      // Blocked storage: it comes back next visit, which is harmless.
    }
  }

  // Write tasks: the prospect does the real thing, then sees the entries it posted.
  const doTasks = [
    {
      icon: FileText,
      title: "Issue an invoice and record a payment",
      body: "Create an invoice, issue it, then record part of the payment and the rest.",
      to: "/invoices/new",
      books: withPeriod("/books/ledger?sourceType=Invoice"),
    },
    {
      icon: ReceiptText,
      title: "Approve and pay a bill",
      body: "Upload or add a bill, approve it, then record a part payment and the final one.",
      to: "/bills",
      books: withPeriod("/books/ledger?sourceType=BillPayment"),
    },
    {
      icon: CreditCard,
      title: "Approve an expense",
      body: "Add a purchase on the demo card, then approve the expense it becomes.",
      to: "/cards",
      books: withPeriod("/books/ledger?sourceType=Expense"),
    },
    {
      icon: Plus,
      title: "Add a bank transaction",
      body: "Put a made-up transaction in the demo bank and watch a rule or the model categorise it.",
      to: withPeriod("/books/bank-lines?add=1"),
      books: withPeriod("/books/ledger?sourceType=BankLine"),
    },
  ];

  const tasks = [
    {
      icon: Landmark,
      title: "Review an uncategorised bank transaction",
      body: "File a bank transaction under an account, with its VAT.",
      to: withPeriod("/books/bank-lines?status=Uncategorised"),
    },
    {
      icon: Link2,
      title: "Match a payment",
      body: "Accept the payment Dolfin suggests for a deposit, or undo an automatic match.",
      to: withPeriod("/books/bank-lines?status=all"),
    },
    {
      icon: Scale,
      title: "Compare the trial balance with Xero",
      body: "Every account beside the platform's, with any difference highlighted.",
      to: withPeriod("/books/trial-balance"),
    },
    {
      icon: SlidersHorizontal,
      title: "Pick a quarter on the period ruler",
      body: "Open the period chip at the top of a report and choose This quarter.",
      to: withPeriod("/books/profit-and-loss"),
    },
  ];

  const row =
    "group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none";

  return (
    <section aria-labelledby="try-it-yourself" className="mb-5 rounded-xl border bg-card/60 p-2 backdrop-blur-sm @sm:p-3">
      <div className="flex items-start justify-between gap-3 px-3 pt-2 pb-1">
        <div>
          <h2 id="try-it-yourself" className="text-sm font-semibold">
            Try it yourself
          </h2>
          <p className="text-xs text-muted-foreground">A few things to do in this demo. Nothing you change leaves Dolfin.</p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss Try it yourself"
          className="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </div>
      <ul className="grid gap-0.5 @4xl:grid-cols-2">
        {doTasks.map((t) => (
          <li key={t.title} className="flex items-center gap-1">
            <Link to={t.to} className={cn(row, "min-w-0 flex-1")}>
              <TaskIcon icon={t.icon} />
              <TaskText title={t.title} body={t.body} />
            </Link>
            <Link
              to={t.books}
              className="shrink-0 rounded-md px-2 py-1 text-xs font-medium whitespace-nowrap text-accent-blue hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            >
              See it in the books
            </Link>
          </li>
        ))}
        {tasks.map((t) => (
          <li key={t.title}>
            <Link to={t.to} className={row}>
              <TaskIcon icon={t.icon} />
              <TaskText title={t.title} body={t.body} />
              <ArrowRight className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
            </Link>
          </li>
        ))}
        <li>
          <button type="button" className={row} onClick={() => openChat(AGENT_PROMPT)}>
            <TaskIcon icon={MessageSquare} />
            <TaskText title={`Ask the agent: "${AGENT_PROMPT}"`} body="It opens with the question ready to send." />
            <ArrowRight className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
          </button>
        </li>
      </ul>
    </section>
  );
}

function TaskIcon({ icon: Icon }: { icon: typeof Landmark }) {
  return (
    <span className="grid size-8 shrink-0 place-items-center rounded-md border bg-background text-muted-foreground">
      <Icon className="size-4" />
    </span>
  );
}

function TaskText({ title, body }: { title: string; body: string }) {
  return (
    <span className={cn("min-w-0 flex-1")}>
      <span className="block text-sm font-medium">{title}</span>
      <span className="block text-xs text-muted-foreground">{body}</span>
    </span>
  );
}
